import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import OverlayMenu from './OverlayMenu';

const FieldHeader = () => {
  const [isFlowing, setIsFlowing] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollDepth, setScrollDepth] = useState(0);
  const [currentSection, setCurrentSection] = useState('hero');

  useEffect(() => {
    const handleScroll = () => {
      const depth = window.scrollY;
      setScrollDepth(depth);
      setIsFlowing(depth > 50);
      
      // Detect current section
      const sections = ['hero', 'current-work', 'books', 'lexicon', 'whitepapers', 'projects', 'journey', 'connection'];
      const currentSectionEl = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSectionEl) {
        setCurrentSection(currentSectionEl);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMenuOpen(false);
    }
  };

  // New sections - positioned to the LEFT of name/image
  const leftNavigationItems = [
    { label: 'Events & Appearances', id: 'events' },
    { label: 'Media', id: 'media' },
    { label: 'Substack', id: 'substack' },
    { label: 'Support this Work', id: 'support' }
  ];

  const navigationFlow = [
    { label: 'Books', id: 'books' },
    { label: 'Lexicon', id: 'lexicon' },
    { label: 'White Papers', id: 'whitepapers' },
    { label: 'Projects', id: 'projects' },
    { label: 'My Journey', id: 'journey' },
    { label: 'Connect', id: 'connection' }
  ];

  // Separate Our Work for right side placement
  const currentWorkItem = { label: 'Our Work', id: 'field-communication' };

  // Show photo on all pages except hero
  const showPhoto = currentSection !== 'hero';

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        isFlowing 
          ? 'bg-slate-900/85 backdrop-blur-xl border-b border-teal-300/25 shadow-field-advanced' 
          : 'bg-transparent'
      }`}>
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            
            {/* CENTER: Logo/Name */}
            <div 
              onClick={() => scrollToSection('hero')}
              className="flex items-center space-x-4 cursor-pointer group transition-all duration-700 hover:scale-105"
            >
              {/* Conditional Teardrop Photo Display */}
              {showPhoto && (
                <div className="relative flex justify-center">
                  <div className="relative w-12 h-12 rounded-full overflow-hidden group-hover:shadow-field-advanced transition-all duration-700">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_water-catalyst/artifacts/hvy4jqca_2a0e5b1d-592e-47b9-b2f6-f27828c2218c.png"
                      alt="Betsy M. Pool"
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                    
                    {/* Subtle animated overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 via-transparent to-teal-400/10 opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
                  </div>
                  
                  {/* Sophisticated ripple effects */}
                  <div className="absolute -inset-2 rounded-full border border-blue-300/25 animate-organic-pulse opacity-40"></div>
                </div>
              )}
              
              <div className="flex flex-col">
                <span className="text-xl font-light tracking-wide text-slate-100 group-hover:text-blue-100 transition-colors duration-600 font-cinematic">
                  Betsy M. Pool
                </span>
                <span className="text-xs text-blue-300/70 font-light tracking-wider">
                  {showPhoto ? 'AI Thought Leader and author on emergent intelligence' : 'Field Intelligence Pioneer'}
                </span>
              </div>
            </div>

            {/* Menu Button - Opens Overlay */}
            <button
              className="text-slate-200 hover:text-teal-200 transition-colors duration-600 p-3 rounded-lg hover:bg-teal-900/20 flex items-center space-x-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <span className="text-sm font-light tracking-wider hidden sm:inline">Menu</span>
              <Menu className="w-6 h-6" />
            </button>
            
          </div>
        </div>

        {/* Sophisticated Flow Indicator */}
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-teal-400/60 to-transparent transform scale-x-0 transition-transform duration-500 water-surface-advanced"
             style={{ transform: isFlowing ? 'scaleX(1)' : 'scaleX(0)' }}></div>
      </header>

      {/* Overlay Menu */}
      <OverlayMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
};

export default FieldHeader;